import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Grid2, Skeleton } from "@mui/material";
import {
  CommonStyles,
  DEFAULT_INPUT_FONT_SIZE,
  DEFAULT_TITLE_FONT_SIZE,
} from "@styles/common-styles";
import { ReactNode } from "react";

/**
 * Defines a single section item that has some content and a label,
 * i.e. label = "First Name" and content = <TextField>
 */
export interface PageSectionItemData {
  /** The label for the item, i.e. "First Name" */
  label?: ReactNode;

  /** Optional flag to indicate if the label text should be bold */
  shouldLabelBold?: boolean;

  /**
   * Optional flag to indicate if this section item should be hidden.
   * If true, the section item will be hidden and it won't visible.
   */
  isHidden?: boolean;

  /** The content of the item, i.e. <TextField> */
  content: JSX.Element;

  /** Optional custom skeleton to be shown when the content is loading */
  customSkeleton?: JSX.Element;

  /** Optional flag to add right padding to the content. Used when displaying buttons */
  shouldAddRightPadding?: boolean;

  /**
   * Optional flag to indicate if the bottom border should hide,
   */
  shouldHideBottomBorder?: boolean;

  /**
   * Optional flag to indicate if the top border should hide,
   */
  shouldHideTopBorder?: boolean;

  /**
   * Optional flag to indicate if the item content should take the full width,
   */
  shouldContentTakeFullWidth?: boolean;

  /** Optional flag to indicate if the item content should be aligned to the end of items or not. */
  shouldAlignToEnd?: boolean;

  /** Optional flag to indicate if the vertical padding should be hidden */
  shouldHidePaddingY?: boolean;

  /** Optional flag to keep label and content in the same row, including small screens */
  shouldKeepContentInlineOnSmallScreens?: boolean;
}

interface Props {
  /** The item to be rendered */
  item: PageSectionItemData;

  /**
   * Flag when the content is loading,
   * if true, the content in all items will be replaced with a skeleton.
   */
  isLoading: boolean;

  /**
   * Flag to indicate that the top border should be shown.
   */
  shouldShowTopBorder?: boolean;
}

/**
 * Default skeleton for the input fields in the page section.
 */
const DEFAULT_INPUT_SKELETON: JSX.Element = (
  <Skeleton variant="text" sx={{ width: "100%", height: "1.875rem" }} />
);

/** Full width grid size for the content column */
const FULL_WIDTH_GRID_SIZE = 12;

/**
 * Defines a single section item that has some content and a label,
 * i.e. label = "First Name" and content = <TextField>
 */
export function PageSectionItem({
  item,
  shouldShowTopBorder,
  isLoading,
}: Props): JSX.Element {
  const { isScreenMdOrSmaller } = useMediaQueryList();
  const labelGridSize = item.shouldKeepContentInlineOnSmallScreens
    ? {
        xs: 6,
        sm: 4,
        md: 6,
        lg: 3,
      }
    : {
        xs: 12,
        md: 6,
        sm: 4,
        lg: 3,
      };

  const contentGridSize = item.shouldKeepContentInlineOnSmallScreens
    ? {
        xs: 6,
        sm: 8,
        md: 6,
        lg: 9,
      }
    : {
        xs: 12,
        md: 6,
        sm: 8,
        lg: 9,
      };

  return (
    // Wrapper for each item in the section
    <Box>
      {/* Grid2 to define two columns in each item */}
      <Grid2
        container
        sx={{
          width: "100%",
          borderTop:
            shouldShowTopBorder && !item.shouldHideTopBorder
              ? CommonStyles.Borders.gray200Divider
              : "none",
          borderBottom: item.shouldHideBottomBorder
            ? "none"
            : CommonStyles.Borders.gray200Divider,
          paddingY: item.shouldHidePaddingY ? "0" : "1.25rem",
          minHeight: "4.375rem",
        }}
      >
        {/* Left column containing the label */}
        {item.label && (
          <Grid2
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: DEFAULT_INPUT_FONT_SIZE,
              fontWeight: item.shouldLabelBold ? "bold" : "normal",
              lineHeight: "1.5rem",
              whiteSpace: item.shouldKeepContentInlineOnSmallScreens
                ? "nowrap"
                : "normal",
            }}
            size={labelGridSize}
          >
            {item.label}
          </Grid2>
        )}
        {/* Right column containing the content */}
        <Grid2
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: item.shouldAlignToEnd ? "flex-end" : "flex-start",
            fontSize: DEFAULT_TITLE_FONT_SIZE,
            lineHeight: "1.5rem",
            paddingRight:
              item.shouldAddRightPadding && !isScreenMdOrSmaller
                ? "1.6875rem"
                : "0",
          }}
          size={
            item.shouldContentTakeFullWidth
              ? FULL_WIDTH_GRID_SIZE
              : contentGridSize
          }
        >
          {isLoading ? (
            item.customSkeleton ?? DEFAULT_INPUT_SKELETON
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment -- needed for JSX
            <>{item.content}</>
          )}
        </Grid2>
      </Grid2>
    </Box>
  );
}
